import React, { useContext, useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { net } from "../../../../database";
import { ClassesContext } from "../../../../Context";

/**
 * ShowTags - Floating popover for tagging staff members in chat replies.
 * Renders via createPortal to avoid z-index/overflow clipping issues.
 * Limits displayed roles to 3 with a "+N more" indicator.
 *
 * @author opencode + glm-5.1
 * @date 2026-04-21
 * @version 2.1
 *
 * @param {Object} props
 * @param {Array} props.staff - Filtered staff list to display
 * @param {number} props.selected - Index of the currently highlighted staff member
 * @param {Object} props.message - The parent message object
 * @param {Function} props.addUser - Callback when a user is selected
 * @param {React.RefObject} props.containerRef - Ref to the input container for positioning
 */
export default function ShowTags(props) {
  const { staff, selected, containerRef } = props;
  const [pos, setPos] = useState({ top: 0, left: 0, width: 280 });

  const updatePosition = useCallback(() => {
    if (!containerRef?.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPos({
      top: rect.top - 8,
      left: rect.left,
      width: 300,
    });
  }, [containerRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [updatePosition]);

  if (!staff || staff.length === 0) return null;

  return createPortal(
    <div className="chat-tags-portal" style={{ top: pos.top, left: pos.left, width: pos.width }}>
      {staff.map((s, i) => (
        <DrawStaff
          staffMember={s}
          key={s._id}
          index={i}
          selectedUser={staff[selected]}
          {...props}
        />
      ))}
    </div>,
    document.body
  );
}

const avatarColors = [
  "bg-teal-600", "bg-cyan-600", "bg-emerald-600",
  "bg-violet-600", "bg-rose-600", "bg-amber-600",
  "bg-sky-600", "bg-indigo-600",
];

function getColorForName(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function getInitials(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function DrawStaff(props) {
  const { staffMember, message, addUser, selectedUser } = props;
  const { classes } = useContext(ClassesContext);

  if (!staffMember) return null;

  const addStaff = () => {
    net
      .post(`/message/${message._id}/add-user`, { newPerson: staffMember._id })
      .then((res) => addUser(res, staffMember));
  };

  const isSelected = selectedUser && staffMember._id === selectedUser._id;

  const filteredRoles = staffMember.roles
    ? staffMember.roles.filter((r) => r !== "Full time" && r !== "Part time").slice(0, 3)
    : [];

  const remainingRoles = staffMember.roles
    ? staffMember.roles.filter((r) => r !== "Full time" && r !== "Part time").length - 3
    : 0;

  const teacherClasses = staffMember.roles?.includes("Teacher")
    ? classes
        .filter(
          (c) =>
            c.teacher === staffMember._id &&
            ["Active", "Tutoring"].includes(c.status)
        )
        .map((c) => c.name)
    : [];

  return (
    <div
      className={`tags-staff ${isSelected ? "tags-staff--selected" : ""}`}
      onClick={() => addStaff()}
    >
      {staffMember.image ? (
        <img
          src={`/images/${staffMember.image}`}
          alt={staffMember.name}
          className="tags-staff-avatar tags-staff-avatar--image"
        />
      ) : (
        <span className={`tags-staff-avatar ${getColorForName(staffMember.name)}`}>
          {getInitials(staffMember.name)}
        </span>
      )}
      <div className="tags-staff-info">
        <div className="tags-staff-name">{staffMember.name}</div>
        <div className="tags-staff-roles">
          {filteredRoles.join(", ")}
          {remainingRoles > 0 && (
            <span className="tags-staff-more">+{remainingRoles}</span>
          )}
        </div>
        {teacherClasses.length > 0 && (
          <div className="tags-staff-classes">{teacherClasses.join(", ")}</div>
        )}
      </div>
    </div>
  );
}
